import { localDateTimeInput } from "../../utils/DateTime";
import { useEffect, useMemo, useRef } from "react";
import { useBasePage } from "../../hooks/useBasePage";
import type { DBModel } from "../../models/DBModel";
import { DataState } from "../../models/DataState";
import type {
  ManagementDialogField,
  ManagementPageProps,
} from "./ManagementPageTypes";
import { getManagementDialogFields } from "./ManagementDialogFields";

interface ManagementPageState<T extends DBModel> {
  isSaving: boolean;
  loading: boolean;
  loadError: string;
  searchText: string;
  status: string;
  isDialogOpen: boolean;
  dialogMode: "Create" | "Edit";
  formValues: Record<string, unknown>;
  rows: T[];
  selectedRow?: T;
  skip: number;
  limit: number;
  totalRecords: number;
  filters: Record<string, string>;
  successMessage: string;
  validationErrors: Record<string, string>;
}

export function useManagementPage<T extends DBModel>(
  props: ManagementPageProps<T>,
) {
  const { state, setStateProperty, setStateProperties } = useBasePage<
    ManagementPageState<T>
  >({
    loading: Boolean(props.Service),
    loadError: "",
    isSaving: false,
    searchText: "",
    status: "All",
    isDialogOpen: false,
    dialogMode: "Create",
    formValues: {},
    rows: props.Rows.map(ensureUnchanged),
    skip: 0,
    limit: props.PageSize ?? 20,
    totalRecords: props.Rows.length,
    filters: {},
    successMessage: "",
    validationErrors: {},
  });
  const loadSequence = useRef(0);
  const searchMounted = useRef(false);
  useEffect(() => {
    if (!searchMounted.current) { searchMounted.current = true; return; }
    ++loadSequence.current;
    const timer = window.setTimeout(() => { void loadPage(0); }, 400);
    return () => window.clearTimeout(timer);
  }, [state.searchText]);
  const savingRef = useRef(false);
  const changedFields = useRef(new Set<string>());
  const reloadRef=useRef<()=>Promise<void>>(async()=>{});
  reloadRef.current=()=>loadPage(state.skip);
  const dialogFields =
    props.DialogFields ?? getManagementDialogFields<T>(props.Title);

  useEffect(() => {
    if (!props.Service) return;
    void loadPage(0);
  }, []);

  useEffect(() => {
    const reload = (): void => {
      void reloadRef.current();
    };
    window.addEventListener("pantrystack:navigation-reselect", reload);
    window.addEventListener("pantrystack:data-changed", reload);
    return () => {
      window.removeEventListener("pantrystack:navigation-reselect", reload);
      window.removeEventListener("pantrystack:data-changed", reload);
    };
  }, []);

  const loadPage = async (skip: number): Promise<void> => {
    if (!props.Service) return;
    const sequence = ++loadSequence.current;
    setStateProperties({loading:true, loadError:""});
    try {
      const result = state.searchText.trim()
        ? await props.Service.SearchByAnyParam(
            state.searchText.trim(),
            skip,
            state.limit,
          )
        : await props.Service.GetAll(skip, state.limit);
      if (sequence !== loadSequence.current) return;
      setStateProperties({
        loading: false,
        rows: result.Data.map(ensureUnchanged),
        skip: result.Skipped ?? skip,
        limit: result.Limit || state.limit,
        totalRecords: result.TotalRecords,
      });
    } catch {
      if (sequence === loadSequence.current) setStateProperties({loading:false,loadError:"Could not load records. Try again; existing rows may be out of date."});
    }
  };

  const filteredRows = useMemo(() => {
    const searchText = state.searchText.trim().toLowerCase();
    return state.rows.filter((row) => {
      const matchesSearch =
        Boolean(props.Service) || !searchText ||
        props.GetSearchText(row).toLowerCase().includes(searchText);
      const matchesStatus =
        state.status === "All" || props.GetStatus?.(row) === state.status;
      const matchesFilters = (props.Filters ?? []).every((filter) => {
        const selected = state.filters[filter.Key];
        return (
          !selected || selected === "All" || filter.GetValue(row) === selected
        );
      });
      const matchesFixedFilter = props.RowFilter?.(row) ?? true;
      return (
        matchesSearch &&
        matchesStatus &&
        matchesFilters &&
        matchesFixedFilter &&
        row.DataState !== DataState.Remove
      );
    });
  }, [props, state.rows, state.searchText, state.status]);

  const openCreateDialog = (): void => {
    if (props.CanCreate === false) return;
    changedFields.current.clear();
    setStateProperties({
      formValues: {
        ...Object.fromEntries(
          dialogFields.map((field) => [field.Key, initialFieldValue(field)]),
        ),
        ...Object.fromEntries(Object.entries(props.DefaultValues ?? {}).map(([key,value])=>{const field=dialogFields.find(f=>f.Key===key);return [key,field?editFieldValue(field,value):value];})),
      },
      selectedRow: undefined,
      dialogMode: "Create",
      isDialogOpen: true,
      validationErrors: {},
    });
  };

  const openEditDialog = (row: T): void => {
    if (props.CanEdit === false || props.CanEditRow?.(row) === false) return;
    changedFields.current.clear();
    setStateProperties({
      // Keep values such as Items that are rendered by custom dialog content
      // and therefore do not appear in DialogFields.
      formValues: {
        ...row,
        ...Object.fromEntries(
          dialogFields.map((field) => [
            field.Key,
            editFieldValue(field, row[field.Key]),
          ]),
        ),
      },
      selectedRow: row,
      dialogMode: "Edit",
      isDialogOpen: true,
      validationErrors: {},
    });
  };

  const closeDialog = (): void => setStateProperty("isDialogOpen", false);

  const setFormValue = (field: string, value: unknown): void => {
    changedFields.current.add(field);
    const nextValues = { ...state.formValues, [field]: value };
    const relatedValues = props.OnFormValueChange?.(
      field as keyof T & string,
      value,
      nextValues,
      { DialogMode: state.dialogMode, ChangedFields: changedFields.current },
    );
    const validationErrors = { ...state.validationErrors };
    delete validationErrors[field];
    Object.keys(relatedValues ?? {}).forEach((key) => {
      delete validationErrors[key];
    });
    setStateProperties({
      formValues: { ...nextValues, ...(relatedValues ?? {}) },
      validationErrors,
    });
    if (state.selectedRow?.DataState === DataState.Unchanged) {
      setStateProperty("selectedRow", {
        ...state.selectedRow,
        DataState: DataState.Changed,
      });
    }
  };

  const save = async (): Promise<void> => {
    if (
      savingRef.current ||
      (state.dialogMode === "Create"
        ? props.CanCreate === false
        : props.CanEdit === false || (state.selectedRow && props.CanEditRow?.(state.selectedRow) === false))
    )
      return;
    const requiredErrors = Object.fromEntries(
      dialogFields
        .filter(
          (field) =>
            field.Required &&
            !(state.dialogMode === "Edit" && field.RemoveAfterNew) &&
            isRequiredValueMissing(state.formValues[field.Key]),
        )
        .map((field) => [field.Key, `${field.Label} is required.`]),
    );
    const validationErrors = {
      ...requiredErrors,
      ...(props.ValidateForm?.(state.formValues) ?? {}),
    };

    if (Object.keys(validationErrors).length > 0) {
      setStateProperty("validationErrors", validationErrors);
      return;
    }

    const dataState =
      state.dialogMode === "Create"
        ? DataState.New
        : state.selectedRow?.DataState === DataState.Unchanged
          ? DataState.Unchanged
          : DataState.Changed;

    const model = {
      ...(state.selectedRow ?? {}),
      ...convertFormValues(dialogFields, state.formValues),
      DataState: dataState,
    } as T;

    savingRef.current = true;
    setStateProperty("isSaving", true);
    try {
      const returnedModels = props.Service
        ? await props.Service.Save([model])
        : [model];

      // Fall back to the submitted model if the API returns an empty array.
      const savedModels = (
        returnedModels.length > 0 ? returnedModels : [model]
      ).map(
        (savedModel) =>
          ({
            ...model,
            ...savedModel,
            DataState: DataState.Unchanged,
          }) as T,
      );

      const savedModelsByKey = new Map(
        savedModels.map((savedModel) => [
          props.GetRowKey(savedModel),
          savedModel,
        ]),
      );

      const existingKeys = new Set(
        state.rows.map((row) => props.GetRowKey(row)),
      );

      // Replace existing rows returned by the save endpoint.
      const updatedRows = state.rows.map(
        (row) => savedModelsByKey.get(props.GetRowKey(row)) ?? row,
      );

      // Add newly created rows that are not already in the table.
      const newRows = savedModels.filter(
        (savedModel) => !existingKeys.has(props.GetRowKey(savedModel)),
      );

      setStateProperties({
        rows: [...newRows, ...updatedRows],
        totalRecords: state.totalRecords + newRows.length,
        selectedRow: undefined,
        isDialogOpen: false,
        successMessage: `${props.Title.replace(/ies$/, "y").replace(/s$/, "")} ${state.dialogMode === "Create" ? "created" : "updated"} successfully.`,
        validationErrors: {},
      });
      props.OnSaved?.(savedModels, state.dialogMode);
      await loadPage(state.skip);
    } catch {
      /* The API interceptor displays the request error; keep the form open. */
    } finally {
      savingRef.current = false;
      setStateProperty("isSaving", false);
    }
  };

  const remove = async (row: T): Promise<void> => {
    if (props.CanDelete === false || props.CanDeleteRow?.(row) === false)
      return;
    const removed = { ...row, DataState: DataState.Remove } as T;
    try {
      if (props.Service?.Delete)
        await props.Service.Delete(props.GetRowKey(row));
      else if (props.Service) await props.Service.Save([removed]);
      setStateProperty(
        "rows",
        state.rows.filter(
          (item) => props.GetRowKey(item) !== props.GetRowKey(row),
        ),
      );
      setStateProperty(
        "successMessage",
        `${props.Title.replace(/ies$/, "y").replace(/s$/, "")} deleted successfully.`,
      );
      setStateProperty("totalRecords", Math.max(0, state.totalRecords - 1));
      await loadPage(state.skip);
    } catch {
      /* Request error is displayed by the API interceptor. */
    }
  };

  const setFilter = (key: string, value: string): void =>
    setStateProperty("filters", { ...state.filters, [key]: value });

  const search = async (): Promise<void> => loadPage(0);
  const previousPage = async (): Promise<void> =>
    loadPage(Math.max(0, state.skip - state.limit));
  const nextPage = async (): Promise<void> =>
    loadPage(state.skip + state.limit);

  return {
    state,
    filteredRows,
    setStateProperty,
    openCreateDialog,
    openEditDialog,
    closeDialog,
    setFormValue,
    save,
    remove,
    dialogFields,
    setFilter,
    search,
    previousPage,
    nextPage,
  };
}

function editFieldValue<T extends DBModel>(
  field: ManagementDialogField<T>,
  value: unknown,
): unknown {
  if (value === null || value === undefined) return initialFieldValue(field);

  // datetime-local cannot display an ISO value with a timezone suffix.
  if (field.Type === "datetime" && typeof value === "string") {
    return localDateTimeInput(value);
  }

  if (field.Type === "date" && typeof value === "string") {
    return value.slice(0, 10);
  }

  return value;
}

function isRequiredValueMissing(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

function ensureUnchanged<T extends DBModel>(row: T): T {
  return { ...row, DataState: row.DataState ?? DataState.Unchanged };
}

function initialFieldValue<T extends DBModel>(
  field: ManagementDialogField<T>,
): unknown {
  if (
    field.Type === "money" ||
    field.Type === "integer" ||
    field.Type === "decimal"
  ) {
    return 0;
  }

  if (field.Type === "multiselect") return [];
  if (field.Type === "switch") return false;

  return "";
}

function convertFormValues<T extends DBModel>(
  fields: ManagementDialogField<T>[],
  values: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(values).map(([key, value]) => {
      const field = fields.find((candidate) => candidate.Key === key);
      const isNumber =
        field?.Type === "integer" ||
        field?.Type === "decimal" ||
        field?.Type === "money";
      const converted =
        field?.Type === "json" && typeof value === "string"
          ? value.trim()
            ? JSON.parse(value)
            : []
          : field?.Type === "datetime" && typeof value === "string" && value
            ? new Date(value).toISOString()
          : isNumber && value !== ""
            ? Number(value)
            : value;
      return [key, converted];
    }),
  );
}
